import * as React from "react";
import { createRefetchContainer, RelayRefetchProp } from "react-relay";
import { Sync, SyncDisabled, Info, Delete } from "@material-ui/icons";
import { Button } from "@material-ui/core";
import { useDispatch } from "react-redux";
import pluralize from "pluralize";
import * as ISchema from "@schema";

// components
import { A, H2, H3 } from "@components/ui";
import { AvatarRow } from "@components/avatar-row";
import { EventSummaryTime } from "@components/event-summary";
import { CalloutCard } from "@clockwise/web-commons/src/ui/callout-card";

// mutations
import { enableOrDisableTeamSyncForEvent } from "@mutations";

// util
import {
  niceNameArrayFromPersonsWithCarryover,
  getAttendeeName,
} from "@clockwise/client-commons/src/util/event";
import { eventFromParent } from "@clockwise/client-commons/src/util/event";
import {
  isTeamCalendarOriginEvent,
  isTeamCalendarSyncDisabled,
  isTeamCalendarSyncEvent,
} from "@util/event.util";
import { ZonedMoment } from "@util/time-zone.util";
import { track, TrackingEvents, trackFSEvent } from "@util/analytics.util";

// state
import { toggleExpanded } from "@actions/settings.actions";
import { setSettingsDialogOpen, setActiveViewEvent } from "@actions/chrome-extension.actions";
import { snackBarOpen } from "@actions/snack-bar.actions";

// internal
import { divider, section, sectionIcon } from "@components/event/Event.styles";
import { fragments, query } from "./EventTeam.gql";
import { TutorialTeamSharedCalendar } from "@components/tutorial-box/tutorial-team-shared-calendar";
import { getCurrentEnvironment } from "@src/state/relay-environment";
import { getValue } from "@src/util/errorable.util";

// styles
import { colors, greys } from "@clockwise/web-commons/src/styles/color.styles";
export { divider, section, sectionIcon } from "@components/event/Event.styles";
import { makeStyles } from "@material-ui/styles";
import classnames from "classnames";
import { logger } from "@clockwise/client-commons/src/util/logger";

const s = {
  divider,
  section,
  sectionIcon,
};

const useStyles = makeStyles({
  flatButton: {
    lineHeight: "24px",
    padding: "0 24px",
    paddingLeft: 8,
    paddingTop: 2,
    paddingRight: 12,
    color: greys.slate,
    justifyContent: "flex-start",
  },
  flatButtonIcon: {
    paddingBottom: 2,
    marginRight: 6,
  },
  manage: {
    color: greys.slate,
    paddingTop: 20,
    fontSize: 14,
    display: "flex",
    flexDirection: "row",
    lineHeight: "20px",
  },
  infoIcon: {
    color: greys.cloudyBlue,
    marginTop: 1,
    marginRight: 8,
    height: 18,
    width: 18,
  },
  team: {
    marginTop: 10,
    border: `1px solid ${greys.borderGrey}`,
    borderRadius: 3,
    padding: 10,

    "& .team-name": {
      fontSize: "14px",
      paddingBottom: 6,
      color: greys.slate,
    },
    "& .attendees-text": {
      fontSize: "12px",
      paddingBottom: 4,
      color: greys.blueGrey,
    },
  },
  originEvent: {
    background: greys.palePaleGrey,
    padding: "15px 12px",
    borderRadius: 8,
    cursor: "pointer",

    "& .time": {
      marginBottom: 10,
    },

    "&:hover": {
      background: greys.paleGrey,
    },
  },
  originEvent__compact: {
    marginTop: 4,
  },
  textProminent: {
    fontSize: 12,
    lineHeight: 1.3,
    color: colors.blueCool.standard,
    fontWeight: 600,
    textTransform: "uppercase",
    marginBottom: 4,
  },
});

type EventTeamComponentProps = {
  org: ISchema.IOrg;
  user: ISchema.IUser;
  smartHoldEventInfo: ISchema.ISmartHoldEventInfo | null;
  eventParent: ISchema.IEventParent;
  calendarId: string;
  relay: RelayRefetchProp;
};

const EventTeamComponent = ({
  smartHoldEventInfo,
  eventParent,
  calendarId,
  org,
  user,
  relay,
}: EventTeamComponentProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  React.useEffect(() => {
    trackFSEvent(TrackingEvents.EVENT.TEAM_SECTION_SHOWN);
  }, []);

  const event = eventFromParent(eventParent, calendarId);
  if (!event) {
    return null;
  }

  const originEvent = smartHoldEventInfo?.originEvent || null;

  const organizerPersonEmails = getValue(event.organizerPerson)?.emails || [];
  const teamCalendarOrigin = isTeamCalendarOriginEvent(event);
  const teamCalendarSyncEvent = isTeamCalendarSyncEvent(event);
  const teamCalendarSyncDisabled = isTeamCalendarSyncDisabled(event);

  const userTeams = getValue(org.userTeams)?.list || [];
  const filteredUserTeams = userTeams.filter(
    (team) =>
      getValue(team.settings)?.manageTeamCalendar &&
      !!team.teamMembers.find((teamMember) => {
        if (organizerPersonEmails.length) {
          return organizerPersonEmails.includes(teamMember.person.primaryCalendarId);
        }

        return getValue(originEvent?.organizerPerson)?.emails.includes(
          teamMember.person.primaryCalendarId,
        );
      }),
  );

  if (
    (!teamCalendarOrigin || !filteredUserTeams.length) &&
    (!teamCalendarSyncEvent || !originEvent)
  ) {
    return null;
  }

  const originEventOrganizerName =
    (originEvent && getAttendeeName(originEvent.organizer)) || "a teammate";
  const team = userTeams.find((team) => event.calendarIds.indexOf(team.teamCalendarId) !== -1);
  const syncDescription = team
    ? `Clockwise is automatically syncing this event from ${originEventOrganizerName}'s calendar to the "${team.teamName}" calendar.`
    : `Clockwise is automatically syncing this event from ${originEventOrganizerName}'s calendar.`;

  const onClickOriginEvent = () => {
    const originEventId = smartHoldEventInfo!.originEvent.eventKey.externalEventId;
    const externalEventId = eventParent.externalEventId;
    const activeCalendarId = smartHoldEventInfo!.originEvent.calendarIds[0] || calendarId;

    dispatch(setActiveViewEvent(originEventId, activeCalendarId, false));
    track(TrackingEvents.EVENT.TEAM_GOTO_ORIGIN_EVENT, { externalEventId, originEventId });
  };

  const onClickSyncButton = () => {
    const event = eventFromParent(eventParent, calendarId);
    if (!event) {
      logger.error("Unable to find event to send active event to chrome extension");
      return;
    }

    const teamCalendarSyncDisabled = isTeamCalendarSyncDisabled(event);
    const isOriginEvent = isTeamCalendarOriginEvent(event) && !isTeamCalendarSyncEvent(event);

    const disable = isOriginEvent ? !teamCalendarSyncDisabled : true;

    const originEventId = isOriginEvent
      ? eventParent.externalEventId
      : smartHoldEventInfo!.originEvent.eventKey.externalEventId;

    enableOrDisableTeamSyncForEvent(
      getCurrentEnvironment(),
      {
        externalEventId: originEventId,
        disable,
        orgRelayId: org.id,
        calendarId: isOriginEvent ? calendarId : org.primaryOrgCalendar,
      },
      () => {
        if (!isOriginEvent) {
          const activeCalendarId = smartHoldEventInfo!.originEvent.calendarIds[0] || calendarId;

          dispatch(
            setActiveViewEvent(
              smartHoldEventInfo!.originEvent.eventKey.externalEventId,
              activeCalendarId,
              false,
            ),
          );
        } else {
          relay.refetch({
            orgRelayId: org.id,
            eventParentRelayId: eventParent.id,
            calendarId: isOriginEvent ? calendarId : org.primaryOrgCalendar,
          });
        }

        dispatch(
          snackBarOpen(
            disable ? "This event will no longer be synced" : "This event is now synced",
          ),
        );
      },
      () =>
        dispatch(
          snackBarOpen("Sorry, there was an issue toggling the sync setting for your event"),
        ),
    );

    track(TrackingEvents.EVENT[isOriginEvent ? "TEAM_TOGGLE_SYNC" : "TEAM_REMOVE_SYNC"], {
      originEventId,
      disabled: disable,
      externalEventId: eventParent.externalEventId,
    });
  };

  const onClickManageSettings = () => {
    const event = eventFromParent(eventParent, calendarId);
    if (!event) {
      logger.error("Unable to find event to send active event to chrome extension");
      return;
    }

    dispatch(setSettingsDialogOpen(true));
    dispatch(toggleExpanded("expandTeam"));

    const isOriginEvent = isTeamCalendarOriginEvent(event);

    track(TrackingEvents.EVENT.TEAM_MANAGE_SETTINGS, { isOriginEvent });
  };

  ///////////////
  // Rendering //
  ///////////////
  const OriginEventSummary = () => {
    if (!originEvent) {
      return null;
    }

    return (
      <div
        className={classnames(classes.originEvent, classes.originEvent__compact)}
        onClick={onClickOriginEvent}
      >
        <H3>{originEvent.title}</H3>
        <div className="time">
          <EventSummaryTime
            org={org}
            event={originEvent}
            startTime={new ZonedMoment(originEvent.startTime.dateOrDateTime)}
            endTime={new ZonedMoment(originEvent.endTime.dateOrDateTime)}
            isAllDay={originEvent.isAllDay}
            calendarId={calendarId}
          />
        </div>
      </div>
    );
  };

  // a user's view of their own OOO calendar event
  if (teamCalendarOrigin && !teamCalendarSyncEvent) {
    const SyncIcon = teamCalendarSyncDisabled ? Sync : SyncDisabled;
    const syncButtonText = `${teamCalendarSyncDisabled ? "Start" : "Stop"} Syncing This Event`;
    const flagsErrorable = user?.chromeExtensionFlagsErrorable;

    return (
      <React.Fragment>
        <div style={s.divider} />
        {flagsErrorable && <TutorialTeamSharedCalendar flagsErrorable={flagsErrorable} />}
        <div style={s.section}>
          <H2>
            <Sync style={s.sectionIcon} />
            Syncing to {filteredUserTeams.length} team{" "}
            {pluralize("calendar", filteredUserTeams.length)}
          </H2>
          {filteredUserTeams.map((team, i) => {
            let overflowCount = 0;

            const persons = [...team.teamMembers, ...team.invitedMembers]
              .map((member) => member.person)
              .filter((person, i) => {
                if (person && i < 3) {
                  return true;
                }

                overflowCount++;
                return false;
              });

            const { niceNameArray } = niceNameArrayFromPersonsWithCarryover(
              persons,
              undefined,
              28,
              undefined,
              true,
              true,
              overflowCount,
            );

            const niceNames = niceNameArray.join("");

            return (
              <div className={classes.team} key={team.teamId || i}>
                <div className="team-name">{team.teamName}</div>
                <div className="attendees-text">{niceNames}</div>
                <div className="avatars">
                  <AvatarRow
                    orgPersons={persons}
                    extraOverflowCount={overflowCount}
                    size="medium"
                    maxAvatarCount={3}
                  />
                </div>
              </div>
            );
          })}
          <Button
            className="cw-mt-2 cw-text-slate-standard"
            onClick={onClickSyncButton}
            variant="outlined"
            fullWidth
          >
            <SyncIcon className={classes.flatButtonIcon} />
            {syncButtonText}
          </Button>
          <div className={classes.manage}>
            <Info className={classes.infoIcon} />
            <div>
              You can <A onClick={onClickManageSettings}>manage your team calendar settings</A> in
              Clockwise settings.
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }

  return (
    <div className="cw-px-2">
      <CalloutCard
        sentiment="info"
        content={{
          header: "Team Calendar Event",
          body: (
            <>
              {syncDescription}
              <OriginEventSummary />
            </>
          ),
        }}
      />
      {team && (
        <Button
          className="cw-mt-4 cw-text-slate-standard"
          onClick={onClickSyncButton}
          variant="outlined"
          fullWidth
        >
          <Delete className={classes.flatButtonIcon} />
          Remove Synced Event
        </Button>
      )}
      <div className={classes.manage}>
        <Info className={classes.infoIcon} />
        <div>
          You can <A onClick={onClickManageSettings}>manage your team calendar settings</A> in
          Clockwise settings.
        </div>
      </div>
    </div>
  );
};

///////////
// Relay //
///////////

export const EventTeam = createRefetchContainer(EventTeamComponent, fragments, query);
